import { CompanyAvatar } from '@/components/CompanyAvatar'
import { cn } from '@/lib/cn'
import { FONT_CLASS, headlineSize, resolveOfferStyle } from './pageStyle'
import type { Offer } from '@shared/model'

/**
 * Erbjudandets egen design, överst i detaljvyn.
 *
 * Samma kupong som i bläddringsläget, i en lägre variant. Tidigare mötte man
 * företagets design när man bläddrade och en helt vit ruta när man öppnade
 * erbjudandet – två olika kuponger för samma rabatt, och den som företaget
 * betalat för syntes bara i den vy man passerar snabbast.
 *
 * Uträkningen delas med OfferPageCard via resolveOfferStyle. Ingen färg och
 * ingen bild bestäms här, bara hur den ska ritas i det här formatet.
 *
 * Bandet går ut till kanterna med negativa marginaler. Modalen har egen
 * padding, och en kupong med luft runtom läser som en bild i en ram i stället
 * för som sidans yta.
 */
export function OfferDetailHeader({
  offer,
  brandColor,
  usedUp,
}: {
  offer: Offer
  brandColor?: string | null
  usedUp?: boolean
}) {
  const s = resolveOfferStyle(offer, brandColor)

  /*
   * En egen sida visas som den är, i sitt eget format.
   *
   * Bandet nedanför är byggt för en rubrik på en färg eller bild. En färdig
   * sida från företaget har redan sin text, och i ett lågt band skulle den
   * antingen beskäras eller krympa till oläslighet. Här får den sin egen
   * höjd efter bildens proportioner, med ett tak så att knappen fortfarande
   * syns utan att man behöver rulla.
   */
  if (s.couponImage) {
    return (
      <div
        className={cn(
          '-mx-5 -mt-2 flex justify-center bg-surface-2',
          usedUp && 'grayscale-[0.95] opacity-45',
        )}
      >
        <img
          src={s.couponImage}
          alt={`${s.headline} hos ${offer.companyName}`}
          className="max-h-[60vh] w-auto max-w-full object-contain"
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative -mx-5 -mt-2 flex flex-col items-center gap-2.5 px-5 py-6 text-center',
        s.surfaceClass,
        // En förbrukad rabatt tappar färgen helt. Nedtonat läser som "laddar",
        // gråskalat och stämplat läser som "slut".
        usedUp && 'grayscale-[0.95] opacity-45',
      )}
      style={s.surface}
    >
      {/* Raster bara där text står. Bär bilden texten själv behövs inget – då
          skulle vi dämpa företagets design i onödan. */}
      {s.hasImage && !s.silent && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0',
            s.onLight ? 'bg-white/55' : 'bg-black/45',
          )}
        />
      )}

      {/*
       * Bär designen texten tiger vår, precis som i bläddringsläget – med ett
       * undantag: bilden får aldrig vara det enda som säger vilket företag det
       * är. En bakgrundsbild finns inte i tillgänglighetsträdet, och den som
       * använder skärmläsare skulle annars möta en sida utan avsändare.
       */}
      {s.silent ? (
        <span className="sr-only">
          {s.headline} hos {offer.companyName}
        </span>
      ) : (
        <>
          <CompanyAvatar name={offer.companyName} logoUrl={offer.companyLogoUrl} size={56} />

          <p
            className={cn(
              'relative text-xs font-bold tracking-[0.16em] uppercase',
              FONT_CLASS[s.style.font],
              s.eyebrowColor,
            )}
          >
            {offer.companyName}
          </p>

          <p
            className={cn(
              'relative leading-none font-extrabold tracking-tight text-balance',
              FONT_CLASS[s.style.font],
              headlineSize(s.style, s.headline),
              s.headlineColor,
            )}
          >
            {s.headline}
          </p>
        </>
      )}

      {/* Höjd även när designen tiger, så att bilden får en yta att fylla. */}
      {s.silent && <div aria-hidden className="h-40 w-full" />}
    </div>
  )
}
